/**
 * Groups consecutive notifications by (entityType, entityId, actorUserId).
 *
 * Only runs of >=2 notifications that share that key AND are within
 * `windowMs` of each other (default 10 minutes) are grouped; everything else
 * is emitted as a single-item group with `isGroup: false`.
 *
 * Pure function — never mutates the input array.
 *
 * @typedef {Object} Notification
 * @property {number|string} id
 * @property {number|string=} actorUserId
 * @property {string=} entityType
 * @property {number|string=} entityId
 * @property {string} createdAt           ISO timestamp
 *
 * @typedef {Object} NotificationGroup
 * @property {string} id                  Stable key for React lists
 * @property {Notification[]} items       Members in the order received
 * @property {boolean} isGroup            True iff items.length > 1
 *
 * @param {Notification[]} notifications  Ordered list (newest first, as the
 *                                        context produces).
 * @param {{ windowMs?: number }} [opts]
 * @returns {NotificationGroup[]}
 */
export function groupConsecutive(notifications, opts = {}) {
  const windowMs = opts.windowMs ?? 10 * 60 * 1000;
  if (!Array.isArray(notifications) || notifications.length === 0) return [];

  const groups = [];
  let current = [notifications[0]];

  for (let i = 1; i < notifications.length; i++) {
    const prev = current[current.length - 1];
    const next = notifications[i];
    if (sameBucket(prev, next) && withinWindow(prev, next, windowMs)) {
      current.push(next);
    } else {
      groups.push(toGroup(current));
      current = [next];
    }
  }
  groups.push(toGroup(current));

  return groups;
}

function sameBucket(a, b) {
  return (
    a.entityType === b.entityType &&
    String(a.entityId ?? '') === String(b.entityId ?? '') &&
    String(a.actorUserId ?? '') === String(b.actorUserId ?? '')
  );
}

function withinWindow(a, b, windowMs) {
  const ta = Date.parse(a.createdAt);
  const tb = Date.parse(b.createdAt);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= windowMs;
}

function toGroup(items) {
  return {
    id: items.length === 1
      ? `single-${items[0].id}`
      : `group-${items[0].id}-${items[items.length - 1].id}`,
    items,
    isGroup: items.length > 1,
  };
}
